import React, { useEffect, useState } from 'react';
import {
  Box, Card, CardContent, Typography, Button, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, IconButton,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField,
  MenuItem, Select, FormControl, InputLabel, Chip, Avatar,
  InputAdornment, Divider,
} from '@mui/material';
import {
  Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon,
  Search as SearchIcon, EditNote as EditNoteIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import apiService from '../../services/apiServices';
import Toast from '../actions/Toasts';
import useConfirm from '../../hooks/useConfirm';

const EMPTY = {
  name: '', code: '', contact_name: '', contact_email: '',
  contact_phone: '', status: 'Active', manager_id: '', agent_id: '', notes: '',
};

export default function ClientsList() {
  const [rows, setRows]           = useState([]);
  const [users, setUsers]         = useState([]);
  const [projects, setProjects]   = useState([]);
  const [selProjects, setSelProjects] = useState([]);   // selected project ids
  const [projSearch, setProjSearch]  = useState('');
  const [openDialog, setOpenDialog]  = useState(false);
  const [editing, setEditing]        = useState(null);
  const [formData, setFormData]      = useState(EMPTY);
  const [toast, setToast]            = useState({ open: false, message: '', variant: 'info' });

  useEffect(() => {
    loadClients();
    loadUsers();
    loadProjects();
  }, []);

  const loadClients = async () => {
    try { const r = await apiService().get('clients'); setRows(r.data || []); }
    catch { setToast({ open: true, message: 'Gabim në ngarkimin e klientëve', variant: 'error' }); }
  };

  const loadUsers = async () => {
    try { const r = await apiService().get('cms_users'); setUsers(r.data || []); } catch {}
  };

  const loadProjects = async () => {
    try { const r = await apiService().get('projects'); setProjects(r.data || []); } catch {}
  };

  const openCreate = () => {
    setEditing(null);
    setFormData(EMPTY);
    setSelProjects([]);
    setProjSearch('');
    setOpenDialog(true);
  };

  const openEdit = (row) => {
    setEditing(row);
    setFormData({
      name:          row.name          || '',
      code:          row.code          || '',
      contact_name:  row.contact_name  || '',
      contact_email: row.contact_email || '',
      contact_phone: row.contact_phone || '',
      status:        row.status        || 'Active',
      manager_id:    row.manager_id    || '',
      agent_id:      row.agent_id      || '',
      notes:         row.notes         || '',
    });
    const linked = projects.filter((p) => p.ClientId === row.id || p.client_id === row.id).map((p) => p.id);
    setSelProjects(linked);
    setProjSearch('');
    setOpenDialog(true);
  };

  const closeDialog = () => { setOpenDialog(false); setEditing(null); };

  const set = (k) => (e) => setFormData((p) => ({ ...p, [k]: e.target.value }));

  const toggleProject = (id) =>
    setSelProjects((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);

  const handleSave = async () => {
    if (!formData.name.trim()) {
      setToast({ open: true, message: 'Emri i klientit është i detyrueshëm', variant: 'error' });
      return;
    }
    try {
      const payload = {
        ...formData,
        manager_id: formData.manager_id || null,
        agent_id:   formData.agent_id   || null,
      };
      let clientId = editing?.id;
      if (editing) {
        await apiService().put('clients', { ...payload, id: editing.id });
        setToast({ open: true, message: 'Klienti u përditësua', variant: 'success' });
      } else {
        const res = await apiService().post('clients', payload);
        clientId = res.data?.id || res.data?.[0]?.id;
        setToast({ open: true, message: 'Klienti u krijua', variant: 'success' });
      }
      // Link selected projects: set their client_id
      if (clientId) {
        const prevLinked = projects
          .filter((p) => p.ClientId === editing?.id || p.client_id === editing?.id)
          .map((p) => p.id);
        const toLink   = selProjects.filter((id) => !prevLinked.includes(id));
        const toUnlink = prevLinked.filter((id) => !selProjects.includes(id));
        await Promise.all([
          ...toLink.map((id) => { const p = projects.find((x) => x.id === id); return p ? apiService().put('projects', { ...p, ClientId: clientId, id }) : null; }),
          ...toUnlink.map((id) => { const p = projects.find((x) => x.id === id); return p ? apiService().put('projects', { ...p, ClientId: null, id }) : null; }),
        ].filter(Boolean));
      }
      closeDialog();
      loadClients();
      loadProjects();
    } catch {
      setToast({ open: true, message: 'Gabim në ruajtjen e klientit', variant: 'error' });
    }
  };

  const [confirm, confirmUI] = useConfirm();


  const handleDelete = async (id) => {
    if (!(await confirm({ title: 'Fshi klientin', message: 'A jeni të sigurt që doni të fshini këtë klient?', confirmLabel: 'Fshi' }))) return;
    try {
      await apiService().delete('clients', id);
      setToast({ open: true, message: 'Klienti u fshi', variant: 'success' });
      loadClients();
    } catch {
      setToast({ open: true, message: 'Gabim në fshirjen e klientit', variant: 'error' });
    }
  };

  const userName = (id) => {
    if (!id) return '—';
    const u = users.find((x) => x.id === id || x.Id === id);
    return u ? (u.name || u.user_name || u.Name) : '—';
  };

  const filteredProjects = projects.filter((p) =>
    !projSearch || p.name?.toLowerCase().includes(projSearch.toLowerCase()) ||
    p.Name?.toLowerCase().includes(projSearch.toLowerCase())
  );

  return (
    <Box sx={{ p: { xs: 1.5, sm: 2, md: 3 } }}>
      {confirmUI}
      <Toast {...toast} />
      <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
        <CardContent sx={{ p: { xs: 1.5, sm: 2.5 }, '&:last-child': { pb: { xs: 1.5, sm: 2.5 } } }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2.5 }}>
            <Typography variant="h5" sx={{ fontWeight: 700 }}>Klientët</Typography>
            <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}
              sx={{ textTransform: 'none', borderRadius: 2, fontWeight: 600 }}>
              Shto Klient
            </Button>
          </Box>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: 'grey.50' }}>
                  <TableCell sx={{ fontWeight: 700 }}>Emri</TableCell>
                  <TableCell sx={{ fontWeight: 700, display: { xs: 'none', sm: 'table-cell' } }}>Kontakti</TableCell>
                  <TableCell sx={{ fontWeight: 700, display: { xs: 'none', md: 'table-cell' } }}>Email</TableCell>
                  <TableCell sx={{ fontWeight: 700, display: { xs: 'none', md: 'table-cell' } }}>Telefoni</TableCell>
                  <TableCell sx={{ fontWeight: 700, display: { xs: 'none', sm: 'table-cell' } }}>Statusi</TableCell>
                  <TableCell sx={{ fontWeight: 700, display: { xs: 'none', lg: 'table-cell' } }}>Menaxheri</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>Veprime</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((row) => (
                  <TableRow key={row.id} hover>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>{row.name}</Typography>
                      {row.code && <Typography variant="caption" color="text.secondary">{row.code}</Typography>}
                    </TableCell>
                    <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>{row.contact_name || '—'}</TableCell>
                    <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>{row.contact_email || '—'}</TableCell>
                    <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>{row.contact_phone || '—'}</TableCell>
                    <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                      <Chip
                        label={row.status === 'Active' || row.status === 'active' ? 'Aktiv' : 'Jo aktiv'}
                        color={row.status === 'Active' || row.status === 'active' ? 'success' : 'default'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell sx={{ display: { xs: 'none', lg: 'table-cell' } }}>
                      <Typography variant="caption">{userName(row.manager_id)}</Typography>
                    </TableCell>
                    <TableCell align="right">
                      <IconButton size="small" color="primary" onClick={() => openEdit(row)}><EditIcon sx={{ fontSize: 18 }} /></IconButton>
                      <IconButton size="small" color="error" onClick={() => handleDelete(row.id)}><DeleteIcon sx={{ fontSize: 18 }} /></IconButton>
                    </TableCell>
                  </TableRow>
                ))}
                {rows.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 5 }}>
                      <Typography color="text.secondary">Nuk ka klientë për të shfaqur</Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* ── Dialog ── */}
      <Dialog open={openDialog} onClose={closeDialog} maxWidth="sm" fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ pb: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box sx={{ width: 44, height: 44, borderRadius: 2, bgcolor: 'primary.main', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <EditNoteIcon sx={{ color: '#fff', fontSize: 22 }} />
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
                {editing ? 'Edito Klientin' : 'Klient i ri'}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Regjistro një klient për ta përdorur në projektet dhe detyrat e tua.
              </Typography>
            </Box>
            <IconButton size="small" onClick={closeDialog}><CloseIcon /></IconButton>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {/* Row 1 */}
            <TextField label="Emri i klientit" required fullWidth placeholder="p.sh. Neptun Sh.p.k."
              value={formData.name} onChange={set('name')} />
            {/* Row 2 */}
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField label="Emri i kontaktit" fullWidth placeholder="p.sh. Arben Gashi"
                value={formData.contact_name} onChange={set('contact_name')} />
              <FormControl fullWidth>
                <InputLabel>Statusi</InputLabel>
                <Select value={formData.status} label="Statusi" onChange={set('status')}>
                  <MenuItem key="active" value="Active">Aktiv</MenuItem>
                  <MenuItem key="inactive" value="Inactive">Jo aktiv</MenuItem>
                </Select>
              </FormControl>
            </Box>
            {/* Row 3 */}
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField label="Email kontakti" fullWidth placeholder="p.sh. finance@klientit.com"
                type="email" value={formData.contact_email} onChange={set('contact_email')} />
              <TextField label="Numri i telefonit" fullWidth placeholder="p.sh. +383 44 123 456"
                value={formData.contact_phone} onChange={set('contact_phone')} />
            </Box>
            {/* Row 4 */}
            <Box sx={{ display: 'flex', gap: 2 }}>
              <FormControl fullWidth>
                <InputLabel>Menaxheri</InputLabel>
                <Select value={formData.manager_id} label="Menaxheri" onChange={set('manager_id')}>
                  <MenuItem key="none-mgr" value=""><em>Zgjedh menaxherin</em></MenuItem>
                  {users.map((u) => (
                    <MenuItem key={u.id || u.Id} value={u.id || u.Id}>{u.name || u.user_name || u.Name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl fullWidth>
                <InputLabel>Agjent</InputLabel>
                <Select value={formData.agent_id} label="Agjent" onChange={set('agent_id')}>
                  <MenuItem key="none-agt" value=""><em>Zgjedh agjentin</em></MenuItem>
                  {users.map((u) => (
                    <MenuItem key={u.id || u.Id} value={u.id || u.Id}>{u.name || u.user_name || u.Name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>

            {/* Linked projects */}
            <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, p: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 1.5 }}>
                <Box sx={{ flex: 1, pr: 2 }}>
                  <Typography variant="body2" sx={{ fontWeight: 700 }}>Projektet e lidhura</Typography>
                  <Typography variant="caption" color="text.secondary">
                    Mund të lidhësh projekte ekzistuese edhe nëse janë të lidhura me klientë të tjerë.
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
                  <Chip
                    label={`${selProjects.length} zgjedhur`}
                    size="small"
                    color={selProjects.length > 0 ? 'primary' : 'default'}
                    sx={{ fontWeight: 700 }}
                  />
                  <TextField
                    size="small"
                    placeholder="Kërko projekte..."
                    value={projSearch}
                    onChange={(e) => setProjSearch(e.target.value)}
                    InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 16 }} /></InputAdornment> }}
                    sx={{ width: 160 }}
                  />
                </Box>
              </Box>
              <Box sx={{ maxHeight: 180, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                {filteredProjects.length === 0 && (
                  <Typography variant="caption" color="text.disabled" sx={{ py: 1, textAlign: 'center', display: 'block' }}>
                    Nuk ka projekte
                  </Typography>
                )}
                {filteredProjects.map((p) => {
                  const id = p.id || p.Id;
                  const selected = selProjects.includes(id);
                  return (
                    <Box
                      key={id}
                      onClick={() => toggleProject(id)}
                      sx={{
                        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                        px: 1.5, py: 1, borderRadius: 1.5, cursor: 'pointer',
                        border: '1px solid', borderColor: selected ? 'primary.main' : 'divider',
                        bgcolor: selected ? 'primary.50' : 'background.paper',
                        '&:hover': { borderColor: 'primary.light', bgcolor: 'action.hover' },
                        transition: 'all .12s',
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Box sx={{
                          width: 18, height: 18, borderRadius: '50%', border: '2px solid',
                          borderColor: selected ? 'primary.main' : 'text.disabled',
                          bgcolor: selected ? 'primary.main' : 'transparent', flexShrink: 0,
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                        }}>
                          {selected && <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: '#fff' }} />}
                        </Box>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>{p.name || p.Name}</Typography>
                      </Box>
                      <Chip
                        label={p.Status || p.status || 'Aktiv'}
                        size="small"
                        variant="outlined"
                        sx={{ height: 20, fontSize: '0.65rem' }}
                      />
                    </Box>
                  );
                })}
              </Box>
            </Box>

            <Divider />

            {/* Notes */}
            <TextField
              label="Koment" fullWidth multiline rows={3}
              placeholder="Shëno detaje shtesë për këtë klient (opsionale)"
              value={formData.notes} onChange={set('notes')}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
          <Button onClick={closeDialog} sx={{ textTransform: 'none', borderRadius: 2, px: 3 }}>Anulo</Button>
          <Button onClick={handleSave} variant="contained"
            sx={{ textTransform: 'none', borderRadius: 2, fontWeight: 700, px: 3 }}>
            {editing ? 'Ruaj ndryshimet' : 'Shto klient'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
